import React, { useMemo, useState } from 'react';
import { radarReports } from '../data/opportunityRadar';

const SAMPLE_PASSWORD = process.env.REACT_APP_RADAR_PASSWORD || 'ThankGod1314';

const momentumLabel = {
  rising: '↑ Rising',
  stable: '→ Stable',
  falling: '↓ Falling',
  new: '● New',
};

const statusTone = (status) => {
  if (status.includes('prototype')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status.includes('Validate')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status.includes('Watch')) return 'bg-violet-50 text-violet-700 border-violet-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const scoreTone = (value) => {
  if (value >= 8.5) return 'text-emerald-700 bg-emerald-50 border-emerald-100';
  if (value >= 8) return 'text-blue-700 bg-blue-50 border-blue-100';
  if (value >= 7) return 'text-amber-700 bg-amber-50 border-amber-100';
  return 'text-slate-600 bg-slate-50 border-slate-100';
};

function LoginGate({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const submit = (event) => {
    event.preventDefault();
    if (password === SAMPLE_PASSWORD) {
      sessionStorage.setItem('lokyisRadarUnlocked', '1');
      onUnlock();
      return;
    }
    setError('Incorrect password');
  };

  return (
    <div className="min-h-screen bg-[#08111f] px-5 py-10 text-white grid place-items-center">
      <div className="w-full max-w-md rounded-[28px] border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur-xl sm:p-9">
        <div className="mb-8">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/55">Private workspace</div>
          <h1 className="mt-5 text-3xl font-bold tracking-tight">Lokyis Opportunity Radar</h1>
          <p className="mt-3 text-sm leading-6 text-white/55">Founder-only research dashboard for tracking product opportunities and evidence over time.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-white/45">Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => { setPassword(event.target.value); setError(''); }}
              className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-3.5 text-white outline-none transition focus:border-violet-300/50 focus:bg-white/[0.13]"
              placeholder="Enter dashboard password"
              autoFocus
            />
          </label>
          {error && <p className="text-sm text-rose-300">{error}</p>}
          <button type="submit" className="w-full rounded-2xl bg-white px-4 py-3.5 text-sm font-bold text-[#08111f] transition hover:bg-white/90">Open Radar</button>
        </form>
        <p className="mt-6 text-xs leading-5 text-white/35">Demo client-side gate only. Use Cloudflare Access for real authentication.</p>
      </div>
    </div>
  );
}

function ScorePill({ label, value }) {
  return (
    <div className={`rounded-2xl border px-3 py-3 ${scoreTone(value)}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-60">{label}</div>
      <div className="mt-1 text-xl font-black">{value.toFixed(1)}</div>
    </div>
  );
}

function Radar() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('lokyisRadarUnlocked') === '1');
  const [selectedId, setSelectedId] = useState(radarReports[0]?.id);
  const report = useMemo(() => radarReports.find((item) => item.id === selectedId) || radarReports[0], [selectedId]);

  if (!unlocked) return <LoginGate onUnlock={() => setUnlocked(true)} />;

  const sourceCount = new Set(report.opportunities.flatMap((item) => item.signals.map((signal) => signal.source))).size;
  const signalCount = report.opportunities.reduce((sum, item) => sum + item.signals.length, 0);
  const topScore = Math.max(...report.opportunities.map((item) => item.scores.overall));

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-900">
      <header className="border-b border-slate-200/80 bg-white/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto flex max-w-[1500px] items-center gap-4 px-5 py-4 lg:px-8">
          <div className="mr-auto">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> Private · Founder workspace
            </div>
            <h1 className="mt-1 text-lg font-bold">Opportunity Radar</h1>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem('lokyisRadarUnlocked'); setUnlocked(false); }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50"
          >
            Lock
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-5 py-6 lg:px-8 lg:py-8">
        <div className="grid gap-6 xl:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 px-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Report history</div>
              <div className="space-y-2">
                {radarReports.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full rounded-2xl px-4 py-3 text-left transition ${item.id === report.id ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/10' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'}`}
                  >
                    <div className="text-sm font-bold">{item.reportDate}</div>
                    <div className={`mt-1 text-xs ${item.id === report.id ? 'text-white/55' : 'text-slate-400'}`}>{item.edition}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Source coverage</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {report.sourceCoverage.map((source) => (
                  <span key={source} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{source}</span>
                ))}
              </div>
              <div className="mt-5 border-t border-slate-100 pt-4 text-xs leading-5 text-slate-400">
                Scan window<br />
                <span className="font-semibold text-slate-600">{report.scanWindow}</span>
              </div>
            </div>
          </aside>

          <div className="min-w-0 space-y-6">
            <section className="overflow-hidden rounded-[30px] bg-[#0c1728] text-white shadow-xl shadow-slate-900/10">
              <div className="grid gap-8 p-6 md:p-8 lg:grid-cols-[1.3fr_0.7fr] lg:p-10">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-violet-300">{report.edition}</div>
                  <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">{report.reportDate}</h2>
                  <p className="mt-4 max-w-3xl text-base leading-7 text-white/65">{report.summary}</p>
                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/40">
                    <span>Generated {report.generatedAt}</span>
                    <span>{signalCount} supporting signals</span>
                    <span>{sourceCount} source organisations</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 self-end">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                    <div className="text-xs text-white/40">Top score</div>
                    <div className="mt-2 text-3xl font-black">{topScore.toFixed(1)}</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                    <div className="text-xs text-white/40">Tracked ideas</div>
                    <div className="mt-2 text-3xl font-black">{report.opportunities.length}</div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Ranked opportunities</div>
                  <h2 className="mt-1 text-2xl font-black">What deserves attention</h2>
                </div>
              </div>

              <div className="space-y-4">
                {report.opportunities.map((item) => (
                  <article key={item.slug} className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
                    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-black text-white">#{item.rank}</span>
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${statusTone(item.status)}`}>{item.status}</span>
                          <span className="text-xs font-semibold text-slate-400">{momentumLabel[item.momentum]}</span>
                        </div>

                        <div className="mt-4 flex flex-wrap items-baseline gap-3">
                          <h3 className="text-2xl font-black tracking-tight">{item.name}</h3>
                          <span className="text-3xl font-black text-slate-900">{item.scores.overall.toFixed(1)}</span>
                          <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-300">overall</span>
                        </div>

                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                          <div className="rounded-2xl bg-slate-50 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Buyer</div>
                            <p className="mt-2 text-sm leading-6 text-slate-700">{item.buyer}</p>
                          </div>
                          <div className="rounded-2xl bg-slate-50 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Problem</div>
                            <p className="mt-2 text-sm leading-6 text-slate-700">{item.problem}</p>
                          </div>
                        </div>

                        <div className="mt-4 rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
                          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-violet-500">Product hypothesis</div>
                          <p className="mt-2 text-sm leading-6 text-slate-700">{item.product}</p>
                        </div>

                        <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">Recommended action</div>
                          <p className="mt-2 text-sm font-medium leading-6 text-slate-700">{item.action}</p>
                        </div>
                      </div>

                      <div>
                        <div className="grid grid-cols-4 gap-2 xl:grid-cols-2">
                          <ScorePill label="24h" value={item.scores.day} />
                          <ScorePill label="7d" value={item.scores.week} />
                          <ScorePill label="30d" value={item.scores.month} />
                          <ScorePill label="90d" value={item.scores.quarter} />
                        </div>

                        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Evidence & provenance</div>
                          <div className="mt-3 space-y-3">
                            {item.signals.map((signal) => (
                              <a key={`${item.slug}-${signal.url}`} href={signal.url} target="_blank" rel="noreferrer" className="block rounded-xl bg-white p-3 transition hover:shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <div className="text-xs font-bold text-slate-800">{signal.source}</div>
                                    <div className="mt-1 text-xs leading-5 text-slate-500">{signal.publication}</div>
                                  </div>
                                  <span className="text-xs text-slate-300">↗</span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-semibold text-slate-400">
                                  <span>{signal.date}</span>
                                  <span>•</span>
                                  <span>{signal.type}</span>
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="grid gap-5 lg:grid-cols-2">
              <div className="rounded-[26px] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-500">Watchlist</div>
                <h2 className="mt-2 text-xl font-black">Interesting, not ready</h2>
                <div className="mt-5 space-y-3">
                  {report.watchlist.map((item) => (
                    <div key={item.name} className="rounded-2xl bg-amber-50 p-4">
                      <div className="font-bold text-slate-800">{item.name}</div>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{item.reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[26px] bg-slate-900 p-6 text-white shadow-sm">
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-violet-300">Accumulated conclusion</div>
                <h2 className="mt-3 text-2xl font-black tracking-tight">{report.conclusion.market}</h2>
                <p className="mt-4 text-sm leading-7 text-white/60">{report.conclusion.thesis}</p>
                <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.06] p-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/35">Next action</div>
                  <p className="mt-2 text-sm font-medium leading-6 text-white/80">{report.conclusion.nextAction}</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Radar;
